import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/States";
import { ChevronRight, ChevronDown, History } from "lucide-react";
import { KMark } from "@/components/KLogo";
import PullToRefresh from "@/components/PullToRefresh";

const STATUS_STYLE = {
  alert: { ring: "hsl(4 90% 58% / 0.4)", bg: "hsl(4 90% 58% / 0.08)", label: "ALERTA", dot: "text-red-400", tag: "🔴" },
  warning: { ring: "hsl(45 95% 55% / 0.4)", bg: "hsl(45 95% 55% / 0.08)", label: "ATENÇÃO", dot: "text-yellow-400", tag: "🟡" },
  normal: { ring: "hsl(150 70% 45% / 0.35)", bg: "hsl(150 70% 45% / 0.07)", label: "TUDO NORMAL", dot: "text-emerald-400", tag: "🟢" },
};

const TZ = "America/Sao_Paulo";

function timeAgo(iso) {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
}

function dayKey(iso) {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ }); // YYYY-MM-DD
}

function dayLabel(iso) {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: TZ });
  const y = new Date(Date.now() - 86400000).toLocaleDateString("en-CA", { timeZone: TZ });
  const k = dayKey(iso);
  if (k === today) return "HOJE";
  if (k === y) return "ONTEM";
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", timeZone: TZ }).toUpperCase();
}

function groupByDay(events) {
  const groups = [];
  const map = {};
  events.forEach((e) => {
    const k = dayKey(e.created_at);
    if (!map[k]) { map[k] = { label: dayLabel(e.created_at), items: [] }; groups.push(map[k]); }
    map[k].items.push(e);
  });
  return groups;
}

export default function Home() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [historyOpen, setHistoryOpen] = useState(false);
  const { data, isLoading, refetch } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/home")).data });
  const { data: notif } = useQuery({ queryKey: ["notif-count"], queryFn: async () => (await api.get("/notifications")).data });
  const unread = notif?.unread || 0;

  if (isLoading) return <Loading label="Carregando seu KONDDO..." />;
  const { greeting, name, condo, alerts = [], history = [], para_voce = [], restricted } = data || {};

  if (restricted) {
    return (
      <div className="space-y-6">
        <div className="float-in">
          <p className="text-lg font-display">{greeting}, <span className="font-bold">{name}</span> 👋</p>
          <p className="text-sm text-muted-foreground">{condo?.name}</p>
        </div>
        <div data-testid="restricted-banner" className="rounded-3xl p-6 border border-yellow-500/30 bg-yellow-500/5 float-in">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 animate-pulse" />
            <p className="font-display font-bold text-yellow-400">Cadastro em análise</p>
          </div>
          <p className="text-sm text-muted-foreground">Seu acesso completo será liberado após aprovação da administração. Enquanto isso, você já está dentro do KONDDO. 💜</p>
        </div>
        <div className="glass rounded-3xl p-6 text-center float-in">
          <div className="inline-flex mb-3"><KMark size={52} /></div>
          <p className="font-display font-semibold">Bem-vindo ao {condo?.name}</p>
          <p className="text-sm text-muted-foreground mt-1">Assim que o síndico aprovar seu cadastro, você verá comunicados, comunidade, serviços e muito mais — automaticamente, sem novo login.</p>
          <button onClick={refresh} data-testid="restricted-refresh" className="mt-4 rounded-full brand-gradient px-5 py-2.5 text-white text-sm font-semibold active:scale-95 transition-transform">Verificar aprovação</button>
        </div>
      </div>
    );
  }

  const historyGroups = groupByDay(history);

  return (
    <PullToRefresh onRefresh={refetch}>
    <div className="space-y-6">
      {/* Greeting */}
      <div className="float-in">
        <p className="text-lg font-display">{greeting}, <span className="font-bold">{name}</span> 👋</p>
        <p className="text-sm text-muted-foreground">{condo?.name}</p>
      </div>

      {/* Para você */}
      <section className="float-in">
        <button onClick={() => nav("/app/notificacoes")} data-testid="para-voce-header" className="flex items-center gap-2 mb-2 active:scale-95 transition-transform">
          <span className="text-xs uppercase tracking-[0.15em] text-primary font-medium">Para você</span>
          {unread > 0 && (
            <span data-testid="para-voce-count" className="min-w-[20px] h-5 px-1.5 rounded-full brand-gradient text-[11px] font-bold text-white flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
        {para_voce.length > 0 ? (
          <div className="glass rounded-3xl divide-y divide-white/5 overflow-hidden">
            {para_voce.map((item, i) => (
              <button key={i} onClick={() => nav(item.link)} data-testid="para-voce-item" className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.04] transition-colors text-left">
                <span className="text-xl">{item.icon}</span>
                <span className="flex-1 text-sm">{item.title}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        ) : (
          <div data-testid="para-voce-empty" className="glass rounded-3xl p-5">
            <p className="text-sm font-medium">Tudo certo por aqui. ✨</p>
            <p className="text-sm text-muted-foreground mt-0.5">Nenhuma pendência para você.</p>
          </div>
        )}
      </section>

      {/* Situação agora */}
      <section className="float-in">
        <p className="text-xs uppercase tracking-[0.15em] text-primary font-medium mb-2">Situação agora</p>
        <div className="space-y-3">
          {alerts.length === 0 ? (
            <div data-testid="situacao-empty" className="rounded-3xl p-5 border card-hover" style={{ borderColor: STATUS_STYLE.normal.ring, background: STATUS_STYLE.normal.bg }}>
              <p className="text-sm font-medium text-emerald-400">🟢 Tudo funcionando normalmente.</p>
            </div>
          ) : (
            alerts.map((a) => {
              const s = STATUS_STYLE[a.type] || STATUS_STYLE.normal;
              return (
                <div key={a.id} data-testid="status-card" className="rounded-3xl p-5 border card-hover" style={{ borderColor: s.ring, background: s.bg }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className={`text-[11px] font-bold tracking-wide ${s.dot}`}>{s.tag} {s.label}</p>
                    {a.pinned && <span className="text-[10px] text-muted-foreground">📌 fixado</span>}
                  </div>
                  <p className="font-display font-semibold">{a.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{a.message}</p>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* AI banner */}
      <button
        data-testid="home-ai-banner"
        onClick={() => document.querySelector('[data-testid="ai-fab"]')?.click()}
        className="w-full text-left rounded-3xl brand-gradient p-5 glow-primary active:scale-[0.99] transition-transform float-in"
      >
        <p className="text-white font-display font-bold flex items-center gap-2">✨ Pergunte ao KONDDO</p>
        <p className="text-white/85 text-sm mt-1">Tire dúvidas sobre regras, horários, reservas e mais.</p>
      </button>

      {/* Histórico do seu condomínio (recolhido) */}
      <section className="float-in">
        <div className="glass rounded-3xl overflow-hidden">
          <button
            data-testid="history-toggle"
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.04] transition-colors"
          >
            <span className="w-9 h-9 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
              <History className="w-4 h-4 text-primary" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Histórico do seu condomínio</p>
              <p className="text-xs text-muted-foreground">Acompanhe as últimas atualizações.</p>
            </div>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${historyOpen ? "rotate-180" : ""}`} />
          </button>

          {historyOpen && (
            <div data-testid="history-panel" className="border-t border-white/5 p-4">
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Nenhuma atualização recente.</p>
              ) : (
                <div className="space-y-4">
                  {historyGroups.map((g, gi) => (
                    <div key={gi}>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2">{g.label}</p>
                      <div className="space-y-2.5">
                        {g.items.map((e) => {
                          const resolved = e.status === "resolved";
                          return (
                            <div key={e.id} data-testid="history-item" className="flex items-start gap-3">
                              <span className="text-xs text-muted-foreground tabular-nums pt-0.5 w-10 shrink-0">{timeAgo(e.created_at)}</span>
                              <span className="text-base leading-none pt-0.5">{e.icon}</span>
                              <p className={`text-sm flex-1 ${resolved ? "text-muted-foreground" : ""}`}>{e.title}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
    </PullToRefresh>
  );
}
